/**
 * Error Tracking Service for Kivity
 * Provides centralized error logging, monitoring, and reporting
 */

interface ErrorReport {
  id: string;
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  componentStack?: string;
  userId?: string;
  userAgent: string;
  url: string;
  metadata?: Record<string, any>;
}

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: string;
  tags?: Record<string, string>;
}

class ErrorTrackingService {
  private static instance: ErrorTrackingService;
  private errorQueue: ErrorReport[] = [];
  private performanceMetrics: PerformanceMetric[] = [];
  private readonly MAX_QUEUE_SIZE = 100;
  private readonly FLUSH_INTERVAL = 60000; // 1 minute
  private flushTimer: NodeJS.Timeout | null = null;

  private constructor() {
    this.setupGlobalErrorHandlers();
    this.startFlushTimer();
  }

  static getInstance(): ErrorTrackingService {
    if (!ErrorTrackingService.instance) {
      ErrorTrackingService.instance = new ErrorTrackingService();
    }
    return ErrorTrackingService.instance;
  }

  private setupGlobalErrorHandlers(): void {
    // Catch unhandled errors
    window.onerror = (message, source, lineno, colno, error) => {
      this.captureError(error || new Error(String(message)), {
        source,
        lineno,
        colno,
      });
      return false;
    };

    // Catch unhandled promise rejections
    window.onunhandledrejection = (event) => {
      this.captureError(
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason)),
        { type: 'unhandledRejection' }
      );
    };

    // Performance monitoring
    if ('PerformanceObserver' in window) {
      try {
        // Monitor long tasks
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              this.trackPerformance('long_task', entry.duration, {
                name: entry.name,
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });

        // Monitor layout shifts
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as any;
            if (!layoutShift.hadRecentInput) {
              this.trackPerformance('layout_shift', layoutShift.value);
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Monitor largest contentful paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.trackPerformance('lcp', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Monitor first input delay
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const firstInput = entry as any;
            this.trackPerformance('fid', firstInput.processingStart - firstInput.startTime);
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // PerformanceObserver not fully supported
        console.warn('Performance monitoring partially unavailable:', e);
      }
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  captureError(
    error: Error,
    metadata?: Record<string, any>,
    userId?: string
  ): void {
    const report: ErrorReport = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'error',
      message: error.message,
      stack: error.stack,
      userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      metadata,
    };

    this.addToQueue(report);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorTracking]', report);
    }
  }

  captureWarning(message: string, metadata?: Record<string, any>): void {
    const report: ErrorReport = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'warning',
      message,
      userAgent: navigator.userAgent,
      url: window.location.href,
      metadata,
    };

    this.addToQueue(report);

    if (process.env.NODE_ENV === 'development') {
      console.warn('[ErrorTracking]', report);
    }
  }

  captureInfo(message: string, metadata?: Record<string, any>): void {
    const report: ErrorReport = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'info',
      message,
      userAgent: navigator.userAgent,
      url: window.location.href,
      metadata,
    };

    this.addToQueue(report);

    if (process.env.NODE_ENV === 'development') {
      console.info('[ErrorTracking]', report);
    }
  }

  captureComponentError(
    error: Error,
    componentStack: string,
    userId?: string
  ): void {
    const report: ErrorReport = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      type: 'error',
      message: error.message,
      stack: error.stack,
      componentStack,
      userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.addToQueue(report);

    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorTracking] Component Error:', report);
    }
  }

  trackPerformance(
    name: string,
    value: number,
    tags?: Record<string, string>
  ): void {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: new Date().toISOString(),
      tags,
    };

    this.performanceMetrics.push(metric);

    // Keep metrics list bounded
    if (this.performanceMetrics.length > this.MAX_QUEUE_SIZE) {
      this.performanceMetrics.shift();
    }

    if (process.env.NODE_ENV === 'development') {
      console.debug('[Performance]', metric);
    }
  }

  private addToQueue(report: ErrorReport): void {
    this.errorQueue.push(report);

    // Prevent queue from growing too large
    if (this.errorQueue.length > this.MAX_QUEUE_SIZE) {
      this.errorQueue.shift();
    }

    // Auto-flush on critical errors
    if (report.type === 'error' && this.errorQueue.length >= 10) {
      this.flush();
    }
  }

  async flush(): Promise<void> {
    if (this.errorQueue.length === 0 && this.performanceMetrics.length === 0) {
      return;
    }

    const errorsToSend = [...this.errorQueue];
    const metricsToSend = [...this.performanceMetrics];

    // Clear queues
    this.errorQueue = [];
    this.performanceMetrics = [];

    // In production, send to backend/analytics service
    // For now, store in localStorage for debugging
    try {
      const existingLogs = JSON.parse(
        localStorage.getItem('kivity_error_logs') || '[]'
      );
      const updatedLogs = [...existingLogs, ...errorsToSend].slice(-500);
      localStorage.setItem('kivity_error_logs', JSON.stringify(updatedLogs));

      const existingMetrics = JSON.parse(
        localStorage.getItem('kivity_performance_metrics') || '[]'
      );
      const updatedMetrics = [...existingMetrics, ...metricsToSend].slice(-500);
      localStorage.setItem(
        'kivity_performance_metrics',
        JSON.stringify(updatedMetrics)
      );

      if (process.env.NODE_ENV === 'development') {
        console.info(
          `[ErrorTracking] Flushed ${errorsToSend.length} errors and ${metricsToSend.length} metrics`
        );
      }
    } catch (e) {
      console.error('Failed to flush error logs:', e);
    }
  }

  getStoredLogs(): ErrorReport[] {
    try {
      return JSON.parse(localStorage.getItem('kivity_error_logs') || '[]');
    } catch {
      return [];
    }
  }

  getPerformanceMetrics(): PerformanceMetric[] {
    try {
      return JSON.parse(
        localStorage.getItem('kivity_performance_metrics') || '[]'
      );
    } catch {
      return [];
    }
  }

  clearLogs(): void {
    localStorage.removeItem('kivity_error_logs');
    localStorage.removeItem('kivity_performance_metrics');
    this.errorQueue = [];
    this.performanceMetrics = [];
  }

  getPerformanceSummary(): Record<string, any> {
    const metrics = this.getPerformanceMetrics();
    const summary: Record<string, any> = {};

    // Group by metric name and calculate averages
    const grouped: Record<string, number[]> = {};
    metrics.forEach((m) => {
      if (!grouped[m.name]) {
        grouped[m.name] = [];
      }
      grouped[m.name].push(m.value);
    });

    Object.keys(grouped).forEach((name) => {
      const values = grouped[name];
      summary[name] = {
        count: values.length,
        avg: values.reduce((a, b) => a + b, 0) / values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        p95: values.sort((a, b) => a - b)[Math.floor(values.length * 0.95)] || 0,
      };
    });

    return summary;
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Export singleton instance
export const errorTracker = ErrorTrackingService.getInstance();

// Export convenience functions
export const captureError = (
  error: Error,
  metadata?: Record<string, any>,
  userId?: string
) => errorTracker.captureError(error, metadata, userId);

export const captureWarning = (
  message: string,
  metadata?: Record<string, any>
) => errorTracker.captureWarning(message, metadata);

export const captureInfo = (message: string, metadata?: Record<string, any>) =>
  errorTracker.captureInfo(message, metadata);

export const trackPerformance = (
  name: string,
  value: number,
  tags?: Record<string, string>
) => errorTracker.trackPerformance(name, value, tags);

export default errorTracker;
